// useNotes — workspaces + flat note list for the visible root.
//
// The server exposes a tree-shaped filesystem (notes.listDir is per
// directory). The new Notes section presents a flat list, so we walk
// the tree client-side. We cap recursion at 4 levels deep and 500
// files total to keep huge note libraries from blowing the request
// budget on first load.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { api, peekCache, type NotesBacklink, type NotesNote, type NotesRoot, type NotesTemplate } from "./rpc";
import { readWalkCache, walkRoot, writeWalkCache, type FlatNote } from "./notes-walk";

export type { FlatNote };

export type NotesSaveState = "idle" | "dirty" | "saving" | "saved" | "error" | "conflict" | "queued";

export interface UseNotes {
  roots: NotesRoot[];
  activeRoot: NotesRoot | null;
  setActiveRoot: (id: string) => void;
  notes: FlatNote[];
  loadingRoots: boolean;
  loadingNotes: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshNotes: () => Promise<void>;
  read: (path: string) => Promise<NotesNote | null>;
  /** Synchronous cache lookup — returns null on miss. Used by the
   *  reader so re-visiting a note paints from cache immediately while
   *  `read` refreshes from the server in the background. */
  peek: (path: string) => NotesNote | null;
  save: (path: string, content: string, expectedSha?: string) => Promise<NotesSaveResult>;
  create: (path: string, content?: string) => Promise<{ ok: boolean; path: string; sha256?: string; error?: string }>;
  rename: (oldPath: string, newPath: string) => Promise<{ ok: boolean; newPath?: string; error?: string }>;
  remove: (path: string) => Promise<{ ok: boolean; error?: string }>;
  uploadAsset: (dataUrl: string) => Promise<{ ok: boolean; relPath?: string; url?: string; error?: string }>;
  listTemplates: () => Promise<NotesTemplate[]>;
  backlinks: (path: string) => Promise<NotesBacklink[]>;
}

// Mirrors the server saveNote return shape. `conflict` carries the
// current on-disk state so the caller can render a merge banner.
export interface NotesSaveResult {
  ok: boolean;
  sha256?: string;
  size?: number;
  mtime?: number;
  error?: string;
  currentSha?: string;
  currentContent?: string;
}

export function useNotes(): UseNotes {
  const [roots, setRoots] = useState<NotesRoot[]>([]);
  const [activeRootId, setActiveRootId] = useState<string | null>(null);
  const [notes, setNotes] = useState<FlatNote[]>([]);
  const [loadingRoots, setLoadingRoots] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // In-memory cache of recently-read notes, keyed by `${root}:${path}`.
  // Lets the reader paint stale content instantly on switch while the
  // network call refreshes in the background. Capped to keep memory
  // sane on very large libraries; the oldest entry gets evicted on
  // overflow.
  const noteCacheRef = useRef<Map<string, NotesNote>>(new Map());
  const NOTE_CACHE_MAX = 60;
  const cacheKey = (path: string) => `${activeRootId || ""}:${path}`;
  const cachePut = (path: string, note: NotesNote) => {
    const m = noteCacheRef.current;
    const k = cacheKey(path);
    // Re-insert pattern keeps insertion order = recency (Map iteration order).
    if (m.has(k)) m.delete(k);
    m.set(k, note);
    if (m.size > NOTE_CACHE_MAX) {
      const oldest = m.keys().next().value;
      if (oldest) m.delete(oldest);
    }
  };
  const cacheDrop = (path: string) => { noteCacheRef.current.delete(cacheKey(path)); };

  const refresh = useCallback(async () => {
    setError(null);

    // SWR: paint cached roots immediately so the section opens without
    // a spinner. The network call below replaces with fresh data.
    let hadCache = false;
    try {
      const cached = await peekCache<{ ok: true; roots: NotesRoot[] }>("notes.listRoots");
      if (cached?.roots?.length) {
        hadCache = true;
        setRoots(cached.roots);
        // Pre-select the active root from the cache so the file-list
        // effect can start hydrating before the network round-trip lands.
        let pick: string | null = null;
        try {
          const saved = localStorage.getItem("hub.notes.activeRoot");
          if (saved && cached.roots.find((r) => r.id === saved)) pick = saved;
        } catch {}
        if (!pick) pick = (cached.roots.find((r) => r.writable) || cached.roots[0])?.id || null;
        setActiveRootId(pick);
        setLoadingRoots(false);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoadingRoots(true);

    try {
      const list = await api.notesListRoots();
      setRoots(list);
      // Restore last-selected root from localStorage, else first writable.
      let pick: string | null = null;
      try {
        const saved = localStorage.getItem("hub.notes.activeRoot");
        if (saved && list.find((r) => r.id === saved)) pick = saved;
      } catch {}
      if (!pick) pick = (list.find((r) => r.writable) || list[0])?.id || null;
      setActiveRootId(pick);
    } catch (e: any) {
      setError(e?.message || "failed to load notes");
    } finally {
      setLoadingRoots(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const refreshNotes = useCallback(async () => {
    if (!activeRootId) { setNotes([]); return; }
    setLoadingNotes(true);
    try {
      const list = await walkRoot(activeRootId);
      list.sort((a, b) => (b.mtimeMs - a.mtimeMs) || a.title.localeCompare(b.title));
      setNotes(list);
    } catch (e: any) {
      setError(e?.message || "failed to list notes");
    } finally {
      setLoadingNotes(false);
    }
  }, [activeRootId]);

  // Walk the selected root.
  useEffect(() => {
    if (!activeRootId) { setNotes([]); return; }
    // Different root → different filesystem scope, so the cached
    // contents from the previous root no longer apply (paths may
    // collide). Clear before re-walking.
    noteCacheRef.current.clear();
    let cancelled = false;

    // SWR: paint the cached walk result instantly so the file list
    // appears without waiting for ~N notes.listDir round-trips. The
    // walkRoot() below runs in the background and replaces the list
    // when fresh data lands. Spinner only on first-ever open of this
    // root.
    let hadCache = false;
    try {
      const cached = readWalkCache()[activeRootId];
      if (cached && cached.length > 0) {
        hadCache = true;
        setNotes(cached);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoadingNotes(true);

    walkRoot(activeRootId)
      .then((list) => {
        if (cancelled) return;
        // Newest first — most natural default for a notes browser.
        list.sort((a, b) => (b.mtimeMs - a.mtimeMs) || a.title.localeCompare(b.title));
        setNotes(list);
        writeWalkCache(activeRootId, list);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message || "failed to list notes"); })
      .finally(() => { if (!cancelled) setLoadingNotes(false); });
    try { if (activeRootId) localStorage.setItem("hub.notes.activeRoot", activeRootId); } catch {}
    return () => { cancelled = true; };
  }, [activeRootId]);

  const setActiveRoot = useCallback((id: string) => { setActiveRootId(id); }, []);

  const read = useCallback(async (path: string) => {
    if (!activeRootId) return null;
    try {
      const note = await api.notesRead(activeRootId, path);
      if (note) cachePut(path, note);
      return note;
    } catch (e: any) { setError(e?.message || "failed to read"); return null; }
    // cachePut closure captures activeRootId via cacheKey; OK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootId]);

  const peek = useCallback((path: string): NotesNote | null => {
    return noteCacheRef.current.get(cacheKey(path)) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootId]);

  // Persist a note. Callers pass the sha they read with, so the server
  // can detect a conflict (another tab / device saved in the
  // meantime). Phase 2a leaves conflict UX simple: surface the error
  // and let the caller decide whether to force-save.
  const save = useCallback(async (path: string, content: string, expectedSha?: string): Promise<NotesSaveResult> => {
    if (!activeRootId) return { ok: false, error: "no root" };
    try {
      const r = await api.notesSave({ root: activeRootId, path, content, expectedSha });
      if (r.ok) {
        // Update cache so a re-open of this note paints with the just-
        // saved content and SHA, not the stale version we last read.
        cachePut(path, { content, sha256: r.sha256, mtimeMs: Date.now(), kind: "markdown" });
      }
      return r;
    } catch (e: any) {
      return { ok: false, error: e?.message || "save failed" };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootId]);

  // Create a new file by saving empty (or default) content. Server's
  // notes.save creates the file if missing. We refresh the list so the
  // new row appears immediately; the caller is expected to set it
  // active afterwards.
  const create = useCallback(async (path: string, content = "") => {
    if (!activeRootId) return { ok: false, path, error: "no root" };
    try {
      const r = await api.notesSave({ root: activeRootId, path, content });
      if (r.ok) {
        cachePut(path, { content, sha256: r.sha256, mtimeMs: Date.now(), kind: "markdown" });
        await refreshNotes();
      }
      return { ok: !!r.ok, path, sha256: r.sha256, error: r.error };
    } catch (e: any) {
      return { ok: false, path, error: e?.message || "create failed" };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootId, refreshNotes]);

  const rename = useCallback(async (oldPath: string, newPath: string) => {
    if (!activeRootId) return { ok: false, error: "no root" };
    try {
      const r = await api.notesRename({ root: activeRootId, oldPath, newPath });
      if (r.ok) {
        // Move the cache entry to the new key — saves a re-fetch right
        // after a rename, which is the most common follow-up action.
        const old = noteCacheRef.current.get(cacheKey(oldPath));
        if (old) {
          cacheDrop(oldPath);
          cachePut(r.newPath || newPath, old);
        }
        await refreshNotes();
      }
      return r;
    } catch (e: any) {
      return { ok: false, error: e?.message || "rename failed" };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootId, refreshNotes]);

  const remove = useCallback(async (path: string) => {
    if (!activeRootId) return { ok: false, error: "no root" };
    try {
      await api.notesDelete(activeRootId, path);
      cacheDrop(path);
      await refreshNotes();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.message || "delete failed" };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootId, refreshNotes]);

  const uploadAsset = useCallback(async (dataUrl: string) => {
    if (!activeRootId) return { ok: false, error: "no root" };
    try {
      return await api.notesUploadAsset({ root: activeRootId, dataUrl });
    } catch (e: any) {
      return { ok: false, error: e?.message || "upload failed" };
    }
  }, [activeRootId]);

  const listTemplates = useCallback(async (): Promise<NotesTemplate[]> => {
    if (!activeRootId) return [];
    try { return await api.notesListTemplates(activeRootId); }
    catch { return []; }
  }, [activeRootId]);

  const backlinks = useCallback(async (path: string): Promise<NotesBacklink[]> => {
    if (!activeRootId) return [];
    try { return await api.notesBacklinks(activeRootId, path); }
    catch { return []; }
  }, [activeRootId]);

  const activeRoot = roots.find((r) => r.id === activeRootId) || null;

  return {
    roots, activeRoot, setActiveRoot,
    notes, loadingRoots, loadingNotes, error,
    refresh, refreshNotes, read, peek,
    save, create, rename, remove, uploadAsset, listTemplates, backlinks,
  };
}
